import type {
  BaseHTMLAttributes,
  ChangeEvent,
  ComponentProps,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
} from 'react'
import { useCallback, useEffect, useState } from 'react'

import { i18n } from '@lingui/core'
import { isNil } from 'ramda'
import type {
  FieldValues,
  UseFormReturn,
  UseFormSetValue,
} from 'react-hook-form'
import { useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { IconButton } from '@twisto/components/atoms/button'
import type { InputProps } from '@twisto/components/atoms/input'
import type { DropDownItemText } from '@twisto/components/molecules/autocomplete/dropdown'
import { usePrevious } from '@twisto/hooks'
import { ChevronDownIcon } from '@twisto/icons'
import { hasString, localeIncludes } from '@twisto/utils'

const focusDebounceDelay = 10

export type Option<T> = ComponentProps<typeof DropDownItemText> & {
  value: T
  id: string
}

type Props<T> = {
  options: Array<Option<T>>
  name: string
  setFormValue?: UseFormSetValue<FieldValues> | undefined
  onOptionSelect?: (selectedSuggestion: Option<T>) => void
  defaultOption?: Option<T>
}

export const useAutocomplete = <T extends unknown>({
  options,
  name,
  onOptionSelect,
  defaultOption,
}: Props<T>) => {
  const { setValue: setFormValue, clearErrors } =
    (useFormContext() as UseFormReturn | null) || {}

  // state which allow user to navigate throw dropdown list by keyboard
  const [focusedSuggestion, setFocusedSuggestion] = useState<number | null>(
    null
  )

  // the value of visible search input
  const [searchInputValue, setSearchInputValue] = useState(
    defaultOption?.primary.toString() ?? ''
  )
  const prevSearchInputValue = usePrevious(searchInputValue)

  // control state of focus
  const [focused, setFocused] = useState(false)
  const [debouncedFocused] = useDebounce(focused, focusDebounceDelay)
  const prevFocused = usePrevious(focused)

  // chosenSuggestion is the value selected from the list of options
  const [selectedOption, setSelectedOption] = useState<Option<T> | null>(
    defaultOption ?? null
  )
  const prevSelectedSuggestion = usePrevious(selectedOption)

  const filteredOptions = options.filter((option) =>
    localeIncludes(option.primary.toString(), searchInputValue)
  )

  // function to clear form
  const resetInputValue = useCallback(() => {
    setSearchInputValue('')
    setSelectedOption(null)

    setFormValue && setFormValue(name, '')
  }, [name, setFormValue])

  // whenever chosen suggestion is changed we need to update value of input
  useEffect(() => {
    if (selectedOption) {
      setSearchInputValue(selectedOption.primary.toString())
    }

    setFormValue && setFormValue(name, selectedOption?.id)
  }, [selectedOption, name, setFormValue])

  useEffect(() => {
    const valueChange = searchInputValue !== prevSearchInputValue

    // if value of input has changed but there is no more text in input...
    // ...we want to reset input
    if (valueChange && !hasString(searchInputValue)) {
      resetInputValue()
    }
  }, [searchInputValue, resetInputValue, focused, prevSearchInputValue])

  useEffect(() => {
    // get the display text from chosen suggestion
    const primaryValue = selectedOption?.primary.toString()
    const isBlurred = prevFocused === false && focused === false

    // check if value has changed on blurred input
    // and compare/check if the current displayed value equals to value of chosen suggestion
    // and if not set value to be same as previously selected suggestion
    if (
      isBlurred &&
      hasString(primaryValue) &&
      hasString(prevSelectedSuggestion?.primary) &&
      searchInputValue !== primaryValue
    ) {
      setSearchInputValue(primaryValue)
    }

    // on blur check if we have value but no suggestion set and reset form
    if (isBlurred && hasString(searchInputValue) && isNil(selectedOption?.id)) {
      resetInputValue()
    }
  }, [
    selectedOption,
    searchInputValue,
    focused,
    prevFocused,
    prevSelectedSuggestion,
    resetInputValue,
  ])

  const handleBlur = () => setFocused(false)
  const handleFocus = ({ target }: FocusEvent<HTMLDivElement>) => {
    if (target.tagName === 'INPUT') {
      setFocusedSuggestion(null)
    }
    setFocused(true)
  }

  // add support for keyboards
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const container = e.currentTarget

    if (
      !debouncedFocused ||
      !options.length ||
      (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')
    ) {
      return
    }

    e.preventDefault()

    if (
      e.key === 'ArrowDown' &&
      (focusedSuggestion === null ||
        focusedSuggestion < filteredOptions.length - 1)
    ) {
      let suggestionToFocus = 0
      if (focusedSuggestion !== null) {
        suggestionToFocus = focusedSuggestion + 1
      }

      setFocusedSuggestion(suggestionToFocus)

      return container.getElementsByTagName('li')[suggestionToFocus].focus()
    }

    if (
      e.key === 'ArrowUp' &&
      focusedSuggestion !== null &&
      focusedSuggestion < options.length
    ) {
      if (focusedSuggestion === 0) {
        setFocusedSuggestion(null)

        return container.getElementsByTagName('input')[0].focus()
      }

      const suggestionToFocus = focusedSuggestion - 1
      setFocusedSuggestion(suggestionToFocus)

      return container.getElementsByTagName('li')[suggestionToFocus].focus()
    }
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value)
  }
  // when user clicks to input we want to preselect current value, so its easier to manipulate with it
  const handleOnClick = (event: MouseEvent<HTMLInputElement>) => {
    event.currentTarget.select()
  }

  const handleOptionSelect = (suggestion: Option<T>) => () => {
    setSelectedOption(suggestion)
    setFocused(false)
    clearErrors?.(name)
    onOptionSelect && onOptionSelect(suggestion)
  }

  const rootProps: BaseHTMLAttributes<HTMLDivElement> = {
    onBlur: handleBlur,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
  }

  const renderedInputProps: InputProps = {
    name: `visible-input-of-${name}`,
    endAdornment: (
      <IconButton
        aria-label={
          /* i18n */ i18n._('components.molecules.autocomplete.ariaLabel')
        }
        color="ghost"
        icon={ChevronDownIcon}
        size="small"
        tabIndex={-1} // turning off the accessibility via 'tab'
      />
    ),
    nativeInputProps: {
      autoComplete: 'new-password', // prevents displaying native autocomplete
    },
    value: searchInputValue,
    onClick: handleOnClick,
    onChange: handleOnChange,
  }

  return {
    rootProps,
    filteredOptions,
    renderedInputProps,
    handleOptionSelect,
    selectedOption,
    openDropdown: debouncedFocused,
  }
}
